import React, { useRef, useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { io } from "socket.io-client";
import adminApi from "../../../utils/admin.api";

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED_EXT = [".xlsx", ".csv"];

const validateFileClient = (file) => {
  if (!file) return null;
  const name = file.name.toLowerCase();
  const okExt = ACCEPTED_EXT.some((ext) => name.endsWith(ext));
  if (!okExt) {
    return "Only .xlsx and .csv files are accepted. Please upload an Excel or CSV file.";
  }
  if (file.size > MAX_BYTES) {
    return "File is too large. Maximum allowed size is 5 MB.";
  }
  if (file.size === 0) {
    return "File appears empty or could not be read.";
  }
  return null;
};

const statusColor = (status) => {
  if (status === "Success") return "success";
  if (status === "Skipped") return "warning";
  return "error";
};

const BulkImportDialog = ({ open, onClose, onComplete }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);

  const reset = () => {
    setFile(null);
    setFileError("");
    setResult(null);
    setProgress(null);
    setUploading(false);
    setSocketConnected(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  useEffect(() => {
    if (!result?.job_id) {
      setProgress(null);
      return;
    }

    const backendUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
    const socket = io(backendUrl, {
      transports: ["websocket", "polling"],
    });

    socket.on("connect", () => {
      setSocketConnected(true);
      console.log("🔌 Connected to Socket.IO bulk import progress channel");
    });

    socket.on("connect_error", (error) => {
      console.error("🔌 Socket.IO connection error:", error);
    });

    socket.on("bulk_import_progress", (data) => {
      if (data.job_id === result.job_id) {
        setProgress(data);
        console.log("⚡ Progress update:", data);
        // If the job is complete, mark as completed to hide progress UI later
        if (data.processed === data.total) {
          // Optionally, you could clear progress after a short delay
          // setTimeout(() => setProgress(null), 3000);
        }
      }
    });

    socket.on("disconnect", () => {
      setSocketConnected(false);
      console.log("🔌 Disconnected from Socket.IO channel");
    });

    return () => {
      socket.disconnect();
    };
  }, [result?.job_id]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0] ?? null;
    setResult(null);
    if (!selected) {
      setFile(null);
      setFileError("");
      return;
    }
    const err = validateFileClient(selected);
    setFile(selected);
    setFileError(err || "");
  };

  const handleSubmit = async () => {
    if (!file || fileError) return;
    setUploading(true);
    setResult(null);
    try {
      const { data } = await adminApi.importUsers(file);
      const payload = data?.data ?? {};
      if (data?.success === false || payload.file_error) {
        setFileError(payload.file_error || data?.message || "Import failed.");
        setResult(null);
      } else {
        setFileError("");
        setResult(payload);
        if (onComplete) onComplete(payload);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = err?.response?.data?.message;
      const payload = err?.response?.data?.data;
      if (payload?.file_error) {
        setFileError(payload.file_error);
      } else {
        setFileError(detail || msg || "Failed to import users.");
      }
    } finally {
      setUploading(false);
    }
  };

  const summary = result?.summary;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Import Users from File
        <IconButton onClick={handleClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a <strong>.xlsx</strong> or <strong>.csv</strong> file (max 5
          MB). The first row must be headers: required columns <em>name</em>,{" "}
          <em>email</em>, <em>password</em>, <em>phone</em>. Optional:{" "}
          <em>designation</em>, <em>purpose</em>, <em>domain</em>,{" "}
          <em>demo_ids</em>
          (comma-separated integers from the demo catalog, e.g.{" "}
          <code>1,2,3</code>).
        </Typography>

        {fileError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {fileError}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
          >
            Select File
            <input
              ref={inputRef}
              type="file"
              hidden
              accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              onChange={handleFileChange}
            />
          </Button>
          {file && (
            <Typography variant="body2" color="text.secondary">
              {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </Typography>
          )}
        </Box>

        {uploading && <LinearProgress sx={{ mb: 2 }} />}

        {summary && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <Chip label={`Total: ${summary.total}`} size="small" />
            <Chip
              label={`Created: ${summary.created}`}
              color="success"
              size="small"
            />
            <Chip
              label={`Failed: ${summary.failed}`}
              color="error"
              size="small"
            />
            <Chip
              label={`Skipped: ${summary.skipped}`}
              color="warning"
              size="small"
            />
          </Box>
        )}

        {result?.job_id && (
          <Box>
            {result?.job_id &&
              progress &&
              progress.processed < progress.total && (
                <Box
                  sx={{
                    mb: 3,
                    p: 2.5,
                    bgcolor: "action.hover",
                    borderRadius: 2,
                    border: "1px dashed",
                    borderColor: "primary.main",
                    transition: "all 0.3s ease",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    📧 Email Dispatch Progress
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontStyle: "italic" }}
                    >
                      (Sending in background)
                    </Typography>
                  </Typography>

                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Processed <strong>{progress.processed}</strong> of{" "}
                        <strong>{progress.total}</strong> users
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", color: "primary.main" }}
                      >
                        {Math.round(
                          (progress.processed / progress.total) * 100,
                        )}
                        %
                      </Typography>
                    </Box>

                    <LinearProgress
                      variant="determinate"
                      value={(progress.processed / progress.total) * 100}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        mb: 1.5,
                        bgcolor: "action.selected",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 4,
                          backgroundImage:
                            "linear-gradient(90deg, #007FFF 0%, #0056b3 100%)",
                        },
                      }}
                    />

                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                      <Chip
                        label={`Succeeded: ${progress.success}`}
                        color="success"
                        variant="outlined"
                        size="small"
                        sx={{ borderRadius: "6px" }}
                      />
                      {progress.failed > 0 && (
                        <Chip
                          label={`Failed: ${progress.failed}`}
                          color="error"
                          variant="outlined"
                          size="small"
                          sx={{ borderRadius: "6px" }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            {/* Show completion message after all emails are processed */}
            {result?.job_id &&
              progress &&
              progress.processed === progress.total && (
                <Box
                  sx={{
                    mb: 3,
                    p: 2.5,
                    bgcolor: "action.hover",
                    borderRadius: 2,
                    border: "1px dashed",
                    borderColor: "success.main",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h6" sx={{ color: "success.main" }}>
                    ✅ All emails dispatched successfully!
                  </Typography>
                </Box>
              )}
            {/* Show preparing state when no progress yet */}
            {result?.job_id && !progress && (
              <Box sx={{ py: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Preparing email dispatch queue...
                </Typography>
                <LinearProgress sx={{ height: 4, borderRadius: 2 }} />
              </Box>
            )}
          </Box>
        )}

        {result?.rows?.length > 0 && (
          <TableContainer
            sx={{
              maxHeight: 320,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.rows.map((r) => (
                  <TableRow key={r.row}>
                    <TableCell>{r.row}</TableCell>
                    <TableCell>{r.name || "—"}</TableCell>
                    <TableCell>{r.email || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        label={r.status}
                        color={statusColor(r.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      {r.reason || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          {result ? "Close" : "Cancel"}
        </Button>
        {!result && (
          <Button
            variant="contained"
            disabled={!file || !!fileError || uploading}
            onClick={handleSubmit}
          >
            {uploading ? "Importing..." : "Import Users"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkImportDialog;
